import { HourglassEmpty } from '@mui/icons-material';
import { Button, CardContent, Grid, Typography, CircularProgress, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import moment from 'moment';
import SimpleCard from '../../../../../components/SimpleCard';
import { APP_ROUTES } from '../../../../../constants/app-routes';

export default function WaitApproval() {
  const navigate = useNavigate();
  const { currentBooking, recentBookings, vendorRetailPayload } = useSelector(
    (store) => store.vendorRetail,
  );
  const [allBookingsComplete, setAllBookingsComplete] = useState(false);

  const handleBackToDashboard = () => {
    navigate(APP_ROUTES.DASHBOARD);
  };

  // Monitor when all bookings are complete
  useEffect(() => {
    const expectedCount = vendorRetailPayload?.expectedBookingCount;

    console.log('🟣 [WaitApproval] expectedCount:', expectedCount);
    console.log('🟣 [WaitApproval] recentBookings.length:', recentBookings?.length);

    // If no expected count is set, assume we're viewing an existing booking (not just created)
    if (!expectedCount) {
      setAllBookingsComplete(true);
      return;
    }

    // Check if all bookings have been created
    if (recentBookings && recentBookings.length >= expectedCount) {
      console.log('🟢 [WaitApproval] All bookings complete!');
      setAllBookingsComplete(true);
    }
  }, [recentBookings, vendorRetailPayload?.expectedBookingCount]);

  // Helper function to format time from HH:mm:ss to 12-hour format
  const formatTime = (timeString) => {
    if (!timeString) return '';
    return moment(timeString, 'HH:mm:ss').format('h:mm A');
  };

  // Calculate total duration and time range from all recent bookings
  const getBookingSummary = () => {
    console.log('🟣 [WaitApproval] currentBooking:', currentBooking);
    console.log('🟣 [WaitApproval] recentBookings:', recentBookings);
    console.log('🟣 [WaitApproval] recentBookings.length:', recentBookings?.length);

    const bookingsToShow = recentBookings.length > 0 ? recentBookings : [currentBooking];
    console.log('🟣 [WaitApproval] bookingsToShow:', bookingsToShow);

    if (!bookingsToShow || bookingsToShow.length === 0 || !bookingsToShow[0].id) {
      return null;
    }

    // Sort bookings by start time
    const sortedBookings = [...bookingsToShow].sort((a, b) =>
      a.start_time.localeCompare(b.start_time),
    );

    const firstBooking = sortedBookings[0];
    const lastBooking = sortedBookings[sortedBookings.length - 1];
    const totalHours = sortedBookings.reduce(
      (sum, booking) => sum + Number(booking.duration_hours),
      0,
    );

    return {
      date: firstBooking.booking_date,
      startTime: firstBooking.start_time,
      endTime: lastBooking.end_time,
      totalHours,
      bookingCount: sortedBookings.length,
    };
  };

  const summary = getBookingSummary();

  return (
    <div>
      <Grid container justifyContent="center" marginBottom={2}>
        <Grid item>
          <Typography fontWeight="bold">Pending Approval</Typography>
        </Grid>
      </Grid>

      <SimpleCard variant="outlined">
        <CardContent>
          <Grid container justifyContent="center" textAlign="center">
            <Grid item xs={12}>
              <HourglassEmpty
                sx={{
                  fontSize: 80,
                  color: '#FFA500',
                  marginBottom: 2,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h5" fontWeight="bold" marginBottom={2}>
                Your application is under review
              </Typography>

              <Typography variant="body1" color="text.secondary" marginBottom={3}>
                The venue owner will review your booking request and get back to you soon.
              </Typography>

              {/* Show loading state while bookings are being created */}
              {!allBookingsComplete && (
                <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: 3 }}>
                  <CircularProgress size={40} sx={{ color: '#3c99ba' }} />
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2, mt: 1 }}>
                    Processing your booking requests...
                  </Typography>
                </Box>
              )}

              {/* Show booking summary only when all bookings are complete */}
              {allBookingsComplete && summary && (
                <div style={{ marginBottom: '2rem' }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Requested Date:</strong> {moment(summary.date).format('MMM DD, YYYY')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Time:</strong> {formatTime(summary.startTime)} -{' '}
                    {formatTime(summary.endTime)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Total Duration:</strong> {summary.totalHours} hour(s)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Time Slots:</strong> {summary.bookingCount}
                  </Typography>
                </div>
              )}

              {/* Show additional info and button only when all bookings are complete */}
              {allBookingsComplete && (
                <>
                  <Typography variant="body2" color="text.secondary" marginBottom={3}>
                    You will receive a notification once the venue owner has made a decision. You
                    can check the status in your dashboard at any time.
                  </Typography>

                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleBackToDashboard}
                    sx={{
                      bgcolor: '#EE7B57',
                      '&:hover': {
                        bgcolor: '#d66a47',
                      },
                      px: 4,
                      py: 1.5,
                    }}>
                    Back to Dashboard
                  </Button>
                </>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </SimpleCard>
    </div>
  );
}
